import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { ServiceProviderProfile, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { useTableState } from '@/hooks/useTableState';
import { DataTablePagination } from '@/components/tables/DataTablePagination';
import { MOCK_USERS_BY_ROLE } from '@/mockData'; // Import mock data

interface ServiceProviderRealDataTableProps {
  currentUserRole: UserRole;
}

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>{children}
  </button>
);

const TableActions: React.FC<{
  serviceProvider: ServiceProviderProfile;
  role: UserRole;
  onAction: (action: string, serviceProviderId: string) => void;
}> = ({ serviceProvider, role, onAction }) => {

  const handleAction = (action: string) => {
    onAction(action, serviceProvider.id);
  };

  if (role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE) {
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('edit')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">Edit</ActionButton>
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
        </div>
      );
  }

  return <></>;
};

const ServiceProviderRealDataTable: React.FC<ServiceProviderRealDataTableProps> = ({ currentUserRole }) => {
  const [serviceProviders, setServiceProviders] = useState<ServiceProviderProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { notify } = useToast();

  const {
    pageIndex,
    pageSize,
    setPageIndex,
    setPageSize,
    getPaginatedData,
  } = useTableState<ServiceProviderProfile>(serviceProviders, 10);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/service-providers.json');
        if (!response.ok) {
          throw new Error(`Failed to fetch service providers: ${response.statusText}`);
        }
        const data: ServiceProviderProfile[] = await response.json();
        setServiceProviders(Array.isArray(data) ? data : [data]);
        notify('Successfully loaded real-time service provider data.', 'success');
      } catch (e: unknown) {
        console.error("API Error:", (e as Error).message);
        // Fallback to mock data
        notify('Could not load real-time data. Displaying mock data instead.', 'warn');
        const mockData = MOCK_USERS_BY_ROLE[UserRole.SERVICE_PROVIDER];
        const mockServiceProviders = (Array.isArray(mockData) ? mockData : [mockData].filter(Boolean)) as unknown as ServiceProviderProfile[];
        setServiceProviders(mockServiceProviders);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [notify]);

  const handleAction = (action: string, serviceProviderId: string) => {
    notify(`Action '${action}' triggered for service provider '${serviceProviderId}'.`, 'info');
    if (action === 'delete') {
      setServiceProviders(currentProviders => currentProviders.filter(p => p.id !== serviceProviderId));
      notify(`Service Provider ${serviceProviderId} was successfully deleted.`, 'success');
    }
  };

  if (isLoading) return <div className="text-center p-8 text-kala-400">Loading service provider data...</div>;

  const paginatedServiceProviders = getPaginatedData();
  const pageCount = Math.ceil(serviceProviders.length / pageSize);

  return (
    <div>
      <Table className="bg-kala-900 text-white rounded-lg p-6">
        <TableCaption>Real-time service provider data. {pageCount > 1 && `(Page ${pageIndex + 1} of ${pageCount})`}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Service</TableHead>
            <TableHead>Rating</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedServiceProviders.map((serviceProvider) => (
            <TableRow key={serviceProvider.id}>
              <TableCell>{serviceProvider.user?.name || 'N/A'}</TableCell>
              <TableCell>{serviceProvider.serviceType}</TableCell>
              <TableCell>{(serviceProvider as { rating?: number; stats?: { rating?: number } })?.rating || (serviceProvider as { rating?: number; stats?: { rating?: number } })?.stats?.rating || 'N/A'}</TableCell>
              <TableCell className="text-right">
                  <TableActions serviceProvider={serviceProvider} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DataTablePagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        pageSize={pageSize}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
        />
    </div>
  );
};

export default ServiceProviderRealDataTable;
